/* eslint-disable no-nested-ternary */
import { useCurrentAccount } from '@application/api';
import { Suspense, lazy } from 'react';
import { ApplicationType } from '@application/types';
import { PageSkeleton } from '@presentation/components';
import { useRouter } from '@application/utils';
import {
  HomeLandingPageController,
  JoinBreederPageController,
  JoinBuyerPageController,
  LoginPageController,
  MtApplicationsBreederPageController,
  MtBreederOnboardingPageController,
  MtLandingBreederPageController,
  MtLandingBuyerPageController,
  NotFoundPageController,
  QRCodePageController,
  SelectPreferredCountryLanguagePageController,
} from '@application/components/controllers';
import { ProfessionalTypeEnum } from '@gql/graphql';
import 'dayjs/locale/fr';
import 'dayjs/locale/nl';
import 'dayjs/locale/de';
import { Navigate, Route, Routes } from 'react-router';
import { RedirectWithStatus } from '../../RedirectWithStatus';
import { MtRoute } from '../MtRoute';

const ArticleListPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/ArticleListPageController'))
      .ArticleListPageController,
  };
});
const ArticlePageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/ArticlePageController'))
      .ArticlePageController,
  };
});
const BookingPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/BookingPageController'))
      .BookingPageController,
  };
});
const BreedListPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/BreedListPageController'))
      .BreedListPageController,
  };
});
const BreedPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/BreedPageController'))
      .BreedPageController,
  };
});
const BreederListPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/BreederListPageController'))
      .BreederListPageController,
  };
});
const BreederPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/BreederPageController'))
      .BreederPageController,
  };
});

const JoinGroomerPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/JoinGroomerPageController'))
      .JoinGroomerPageController,
  };
});
const JoinLandingPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/JoinLandingPageController'))
      .JoinLandingPageController,
  };
});
const JoinShelterPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/JoinShelterPageController'))
      .JoinShelterPageController,
  };
});
const JoinVetPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/JoinVetPageController'))
      .JoinVetPageController,
  };
});
const LitterListPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/LitterListPageController'))
      .LitterListPageController,
  };
});
const LitterPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/LitterPageController'))
      .LitterPageController,
  };
});
const ProLandingPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/ProLandingPageController'))
      .ProLandingPageController,
  };
});
const QuizPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/QuizPageController'))
      .QuizPageController,
  };
});
const AgendaConfigPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/AgendaConfigPageController'))
      .AgendaConfigPageController,
  };
});
const ApplicationPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/ApplicationPageController'))
      .ApplicationPageController,
  };
});
const BreedComparatorPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/BreedComparatorController'))
      .BreedComparatorPageController,
  };
});
const BreederLandingPageController = lazy(async () => {
  return {
    default: (
      await import('@application/components/controllers/pages/BreederLandingPageController')
    ).BreederLandingPageController,
  };
});
const BreederLinksPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/BreederLinksPageController'))
      .BreederLinksPageController,
  };
});
const BreederReviewPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/BreederReviewPageController'))
      .BreederReviewPageController,
  };
});
const ChartPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/ChartPageController'))
      .ChartPageController,
  };
});
const ContactPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/ContactPageController'))
      .ContactPageController,
  };
});
const CookiesPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/CookiesPageController'))
      .CookiesPageController,
  };
});
const EducatorPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/EducatorPageController'))
      .EducatorPageController,
  };
});
const EducatorReviewPageController = lazy(async () => {
  return {
    default: (
      await import('@application/components/controllers/pages/EducatorReviewPageController')
    ).EducatorReviewPageController,
  };
});
const FaqPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/FaqPageController'))
      .FaqPageController,
  };
});
const JoinEducatorPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/JoinEducatorPageController'))
      .JoinEducatorPageController,
  };
});
const JoinProInfoAgendaPageController = lazy(async () => {
  return {
    default: (
      await import('@application/components/controllers/pages/JoinProInfoAgendaPageController')
    ).JoinProInfoAgendaPageController,
  };
});
const JoinProInfoBookingPageController = lazy(async () => {
  return {
    default: (
      await import('@application/components/controllers/pages/JoinProInfoBookingPageController')
    ).JoinProInfoBookingPageController,
  };
});
const JoinProInfoProfilePageController = lazy(async () => {
  return {
    default: (
      await import('@application/components/controllers/pages/JoinProInfoProfilePageController')
    ).JoinProInfoProfilePageController,
  };
});
const JoinProInfoSeoPageController = lazy(async () => {
  return {
    default: (
      await import('@application/components/controllers/pages/JoinProInfoSeoPageController')
    ).JoinProInfoSeoPageController,
  };
});
const LitterLinksPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/LitterLinksPageController'))
      .LitterLinksPageController,
  };
});
const LocationLinksPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/LocationLinksPageController'))
      .LocationLinksPageController,
  };
});

const MtAdvantagesPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/MtAdvantagesPageController'))
      .MtAdvantagesPageController,
  };
});
const MtAnimalBreederPageController = lazy(async () => {
  return {
    default: (
      await import('@application/components/controllers/pages/MtAnimalBreederPageController')
    ).MtAnimalBreederPageController,
  };
});
const MtAnimalBuyerPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/MtAnimalBuyerPageController'))
      .MtAnimalBuyerPageController,
  };
});
const MtAnimalsBreederPageController = lazy(async () => {
  return {
    default: (
      await import('@application/components/controllers/pages/MtAnimalsBreederPageController')
    ).MtAnimalsBreederPageController,
  };
});
const MtAnimalsBuyerPageController = lazy(async () => {
  return {
    default: (
      await import('@application/components/controllers/pages/MtAnimalsBuyerPageController')
    ).MtAnimalsBuyerPageController,
  };
});
const MtApplicationAcceptSuccessPageController = lazy(async () => {
  return {
    default: (
      await import(
        '@application/components/controllers/pages/MtApplicationAcceptSuccessPageController'
      )
    ).MtApplicationAcceptSuccessPageController,
  };
});
const MtApplicationBreederPageController = lazy(async () => {
  return {
    default: (
      await import('@application/components/controllers/pages/MtApplicationBreederPageController')
    ).MtApplicationBreederPageController,
  };
});
const MtApplicationBuyerPageController = lazy(async () => {
  return {
    default: (
      await import('@application/components/controllers/pages/MtApplicationBuyerPageController')
    ).MtApplicationBuyerPageController,
  };
});
const MtApplicationCheckoutFailedPageController = lazy(async () => {
  return {
    default: (
      await import(
        '@application/components/controllers/pages/MtApplicationCheckoutFailedPageController'
      )
    ).MtApplicationCheckoutFailedPageController,
  };
});

const MtApplicationsBuyerPageController = lazy(async () => {
  return {
    default: (
      await import('@application/components/controllers/pages/MtApplicationsBuyerPageController')
    ).MtApplicationsBuyerPageController,
  };
});
const MtBookingsPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/MtBookingsPageController'))
      .MtBookingsPageController,
  };
});
const MtContactPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/MtContactPageController'))
      .MtContactPageController,
  };
});
const MtFaqPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/MtFaqPageController'))
      .MtFaqPageController,
  };
});

const MtLitterPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/MtLitterPageController'))
      .MtLitterPageController,
  };
});
const MtLittersPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/MtLittersPageController'))
      .MtLittersPageController,
  };
});
const MtMessagesPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/MtMessagesPageController'))
      .MtMessagesPageController,
  };
});
const MtPaymentsPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/MtPaymentsPageController'))
      .MtPaymentsPageController,
  };
});
const MtProfileBreederPageController = lazy(async () => {
  return {
    default: (
      await import('@application/components/controllers/pages/MtProfileBreederPageController')
    ).MtProfileBreederPageController,
  };
});
const MtOnboardingPageController = lazy(async () => {
  return {
    default: (
      await import('@application/components/controllers/pages/MtBreederOnboardingPageController')
    ).MtBreederOnboardingPageController,
  };
});
const MtPuppyGuidePageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/MtPuppyGuidePageController'))
      .MtPuppyGuidePageController,
  };
});
const MtSettingsBreederPageController = lazy(async () => {
  return {
    default: (
      await import('@application/components/controllers/pages/MtSettingsBreederPageController')
    ).MtSettingsBreederPageController,
  };
});
const MtSettingsBuyerPageController = lazy(async () => {
  return {
    default: (
      await import('@application/components/controllers/pages/MtSettingsBuyerPageController')
    ).MtSettingsBuyerPageController,
  };
});
const MtWishlistPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/MtWishlistPageController'))
      .MtWishlistPageController,
  };
});
const OldProRedirectionController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/OldProRedirectionController'))
      .OldProRedirectionController,
  };
});
const PrivacyPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/PrivacyPageController'))
      .PrivacyPageController,
  };
});
const ProListPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/ProListPageController'))
      .ProListPageController,
  };
});
const ProPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/ProPageController'))
      .ProPageController,
  };
});
const QuizResultsPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/QuizResultsPageController'))
      .QuizResultsPageController,
  };
});
const ResetPasswordPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/ResetPasswordPageController'))
      .ResetPasswordPageController,
  };
});

const ShelterListPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/ShelterListPageController'))
      .ShelterListPageController,
  };
});
const ShelterPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/ShelterPageController'))
      .ShelterPageController,
  };
});
const StoryPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/StoryPageController'))
      .StoryPageController,
  };
});
const StudListPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/StudListPageController'))
      .StudListPageController,
  };
});
const StudPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/StudPageController'))
      .StudPageController,
  };
});
const TeamPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/TeamPageController'))
      .TeamPageController,
  };
});
const TermsPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/TermsPageController'))
      .TermsPageController,
  };
});
const UnsubscribePageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/UnsubscribePageController'))
      .UnsubscribePageController,
  };
});
const UnsubscribePublicPageController = lazy(async () => {
  return {
    default: (
      await import('@application/components/controllers/pages/UnsubscribePublicPageController')
    ).UnsubscribePublicPageController,
  };
});
const ImpressumPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/ImpressumPageController'))
      .ImpressumPageController,
  };
});
const ThankyouPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/ThankyouPageController'))
      .ThankyouPageController,
  };
});

const FeedbackPageController = lazy(async () => {
  return {
    default: (await import('@application/components/controllers/pages/FeedbackPageController'))
      .FeedbackPageController,
  };
});
export const AppRoutes = () => {
  const { translatedRoute } = useRouter();
  const { currentAccountLoggedIn, currentAccount, currentAccountType } = useCurrentAccount();

  return (
    <Suspense fallback={<PageSkeleton />}>
      <Routes>
        <Route
          path="/country-selector"
          element={<SelectPreferredCountryLanguagePageController />}
        />
        <Route path={translatedRoute('homeLanding')} element={<HomeLandingPageController />} />
        <Route path={`${translatedRoute('article')}:id`} element={<ArticlePageController />} />
        <Route path={translatedRoute('articleList')} element={<ArticleListPageController />} />
        <Route path={`${translatedRoute('breed')}:id`} element={<BreedPageController />} />
        <Route path={`${translatedRoute('breeder')}/*`} element={<BreederPageController />} />
        <Route
          path={`${translatedRoute('breederReview')}/*`}
          element={<BreederReviewPageController />}
        />
        <Route
          path={`${translatedRoute('breederList')}/*`}
          element={<BreederListPageController />}
        />
        <Route path={translatedRoute('breederLinks')} element={<BreederLinksPageController />} />
        <Route path={translatedRoute('breedList')} element={<BreedListPageController />} />
        <Route
          path={`${translatedRoute('groomer')}:id`}
          element={<ProPageController type={ProfessionalTypeEnum.GROOMER} />}
        />
        <Route
          path={`${translatedRoute('groomerList')}/*`}
          element={
            <ProListPageController
              type={ProfessionalTypeEnum.GROOMER}
              key={ProfessionalTypeEnum.GROOMER}
            />
          }
        />
        <Route
          path={`${translatedRoute('educatorList')}/*`}
          element={
            <ProListPageController
              type={ProfessionalTypeEnum.EDUCATOR}
              key={ProfessionalTypeEnum.EDUCATOR}
            />
          }
        />
        <Route path={`${translatedRoute('educator')}/*`} element={<EducatorPageController />} />
        <Route
          path={translatedRoute('breederLanding')}
          element={<BreederLandingPageController />}
        />
        <Route
          path={translatedRoute('joinBreeder')}
          element={
            currentAccount ? (
              <Navigate replace to={translatedRoute('mtLanding')} />
            ) : (
              <JoinBreederPageController />
            )
          }
        />
        <Route
          path={translatedRoute('joinBuyer')}
          element={
            currentAccount ? (
              <Navigate replace to={translatedRoute('mtLanding')} />
            ) : (
              <JoinBuyerPageController />
            )
          }
        />
        <Route path={translatedRoute('joinGroomer')} element={<JoinGroomerPageController />} />
        <Route path={translatedRoute('joinLanding')} element={<JoinLandingPageController />} />
        <Route path={translatedRoute('joinShelter')} element={<JoinShelterPageController />} />
        <Route path={translatedRoute('joinVet')} element={<JoinVetPageController />} />
        <Route
          path={translatedRoute('joinProInfoBookingVet')}
          element={<JoinProInfoBookingPageController type={ProfessionalTypeEnum.VET} />}
        />
        <Route
          path={translatedRoute('joinProInfoProfileVet')}
          element={<JoinProInfoProfilePageController type={ProfessionalTypeEnum.VET} />}
        />
        <Route
          path={translatedRoute('joinProInfoSeoVet')}
          element={<JoinProInfoSeoPageController type={ProfessionalTypeEnum.VET} />}
        />
        <Route
          path={translatedRoute('joinProInfoAgendaVet')}
          element={<JoinProInfoAgendaPageController type={ProfessionalTypeEnum.VET} />}
        />
        <Route
          path={translatedRoute('joinProInfoBookingGroomer')}
          element={<JoinProInfoBookingPageController type={ProfessionalTypeEnum.GROOMER} />}
        />
        <Route
          path={translatedRoute('joinProInfoProfileGroomer')}
          element={<JoinProInfoProfilePageController type={ProfessionalTypeEnum.GROOMER} />}
        />
        <Route
          path={translatedRoute('joinProInfoSeoGroomer')}
          element={<JoinProInfoSeoPageController type={ProfessionalTypeEnum.GROOMER} />}
        />
        <Route
          path={translatedRoute('joinProInfoAgendaGroomer')}
          element={<JoinProInfoAgendaPageController type={ProfessionalTypeEnum.GROOMER} />}
        />
        <Route path={translatedRoute('litterLinks')} element={<LitterLinksPageController />} />
        <Route path={`${translatedRoute('litter')}/*`} element={<LitterPageController />} />
        <Route path={`${translatedRoute('litterList')}/*`} element={<LitterListPageController />} />
        <Route
          path={translatedRoute('login')}
          element={
            currentAccountLoggedIn ? (
              currentAccountType === 'breeder' && currentAccount?.dogBreeder?.certified ? (
                <Navigate replace to={translatedRoute('mtApplications')} />
              ) : (
                <Navigate replace to={translatedRoute('mtLanding')} />
              )
            ) : (
              <LoginPageController />
            )
          }
        />
        <Route path={translatedRoute('proLanding')} element={<ProLandingPageController />} />
        <Route path={`${translatedRoute('quiz')}/*`} element={<QuizPageController />} />
        <Route path={`${translatedRoute('quizResults')}`} element={<QuizResultsPageController />} />
        <Route path={`${translatedRoute('terms')}`} element={<TermsPageController />} />
        <Route path={`${translatedRoute('impressum')}`} element={<ImpressumPageController />} />
        <Route path={`${translatedRoute('thankyou')}`} element={<ThankyouPageController />} />
        <Route path={`${translatedRoute('qrcode')}:id`} element={<QRCodePageController />} />
        <Route path={`${translatedRoute('qrcode')}`} element={<ThankyouPageController />} />
        <Route path={`${translatedRoute('feedback')}`} element={<FeedbackPageController />} />
        <Route path={`${translatedRoute('team')}`} element={<TeamPageController />} />
        <Route path={`${translatedRoute('privacy')}`} element={<PrivacyPageController />} />
        <Route path={`${translatedRoute('chart')}`} element={<ChartPageController />} />
        <Route path={`${translatedRoute('cookies')}`} element={<CookiesPageController />} />
        <Route path={`${translatedRoute('contact')}`} element={<ContactPageController />} />
        <Route path={`${translatedRoute('story')}`} element={<StoryPageController />} />
        <Route
          path={`${translatedRoute('vetLocationLinks')}/*`}
          element={<LocationLinksPageController proType={ProfessionalTypeEnum.VET} />}
        />
        <Route
          path={`${translatedRoute('groomerLocationLinks')}/*`}
          element={<LocationLinksPageController proType={ProfessionalTypeEnum.GROOMER} />}
        />
        <Route
          path={`${translatedRoute('educatorLocationLinks')}/*`}
          element={<LocationLinksPageController proType={ProfessionalTypeEnum.EDUCATOR} />}
        />
        <Route path={`${translatedRoute('stud')}/*`} element={<StudPageController />} />
        <Route path={translatedRoute('studList')} element={<StudListPageController />} />
        <Route path={`${translatedRoute('shelter')}/*`} element={<ShelterPageController />} />
        <Route
          path={`${translatedRoute('shelterList')}/*`}
          element={<ShelterListPageController />}
        />
        <Route
          path={`${translatedRoute('vet')}/*`}
          element={<ProPageController type={ProfessionalTypeEnum.VET} />}
        />
        <Route
          path={`${translatedRoute('vetList')}/*`}
          element={
            <ProListPageController type={ProfessionalTypeEnum.VET} key={ProfessionalTypeEnum.VET} />
          }
        />
        <Route path={translatedRoute('booking')} element={<BookingPageController />} />
        <Route path={translatedRoute('agendaConfig')} element={<AgendaConfigPageController />} />
        <Route
          path={`${translatedRoute('resetPassword')}/*`}
          element={<ResetPasswordPageController />}
        />
        <Route
          path={`${translatedRoute('application')}/*`}
          element={<ApplicationPageController />}
        />
        <Route
          path={translatedRoute('faqBuyer')}
          element={<FaqPageController resource="buyer" />}
        />
        <Route
          path={`${translatedRoute('faqBreeder')}/*`}
          element={<FaqPageController resource="breeder" />}
        />
        <Route
          path={`${translatedRoute('breedComparator')}/*`}
          element={<BreedComparatorPageController />}
        />
        <Route
          path={`${translatedRoute('publicUnsubscribe')}/*`}
          element={<UnsubscribePublicPageController />}
        />
        <Route
          path={`${translatedRoute('unsubscribe')}/*`}
          element={<UnsubscribePageController />}
        />

        {/* Mt (MyTipaw) routes */}
        <Route
          path={translatedRoute('mtLanding')}
          element={
            <MtRoute
              breeder={<MtLandingBreederPageController />}
              buyer={<MtLandingBuyerPageController />}
            />
          }
        />
        <Route
          path={`${translatedRoute('mtProfile')}/*`}
          element={
            <MtRoute
              breeder={<MtBreederOnboardingPageController action="edit" />}
              buyer={<MtSettingsBuyerPageController />}
            />
          }
        />
        <Route
          path={`${translatedRoute('mtBreederProfile')}/*`}
          element={
            <MtRoute
              breeder={<MtProfileBreederPageController />}
              buyer={<MtSettingsBuyerPageController />}
            />
          }
        />
        <Route
          path={translatedRoute('mtOnboarding')}
          element={
            <MtRoute
              breeder={<MtOnboardingPageController action="new" />}
              buyer={<MtSettingsBuyerPageController />}
            />
          }
        />
        <Route
          path={`${translatedRoute('mtPage')}/*`}
          element={<MtRoute breeder={<MtProfileBreederPageController />} />}
        />
        <Route
          path={`${translatedRoute('mtAppointments')}/*`}
          element={<MtRoute buyer={<MtBookingsPageController />} />}
        />
        <Route
          path={translatedRoute('mtAnimals')}
          element={
            <MtRoute
              breeder={<MtAnimalsBreederPageController />}
              buyer={<MtAnimalsBuyerPageController />}
            />
          }
        />
        <Route
          path={`${translatedRoute('mtAnimals')}:id`}
          element={
            <MtRoute
              breeder={<MtAnimalBreederPageController />}
              buyer={<MtAnimalBuyerPageController />}
            />
          }
        />
        <Route
          path={translatedRoute('mtLitters')}
          element={<MtRoute breeder={<MtLittersPageController />} />}
        />
        <Route
          path={`${translatedRoute('mtLitters')}:id`}
          element={<MtRoute breeder={<MtLitterPageController />} />}
        />
        <Route
          path={translatedRoute('mtWishlist')}
          element={<MtRoute buyer={<MtWishlistPageController />} />}
        />
        <Route
          path={`${translatedRoute('mtMessages')}:otherAccountId?`}
          element={
            <MtRoute buyer={<MtMessagesPageController />} breeder={<MtMessagesPageController />} />
          }
        />
        <Route
          path={translatedRoute('mtAdvantages')}
          element={
            <MtRoute
              breeder={<MtAdvantagesPageController resource="breeder" />}
              // buyer={<MtAdvantagesPageController resource="buyer" />} TC-1142 - removed advantages for buyers
            />
          }
        />
        <Route
          path={translatedRoute('mtPuppyGuide')}
          element={<MtRoute buyer={<MtPuppyGuidePageController />} />}
        />
        <Route
          path={translatedRoute('mtContact')}
          element={
            <MtRoute buyer={<MtContactPageController />} breeder={<MtContactPageController />} />
          }
        />
        <Route
          path={translatedRoute('mtFaq')}
          element={
            <MtRoute
              buyer={<MtFaqPageController resource="buyer" />}
              breeder={<MtFaqPageController resource="breeder" />}
            />
          }
        />
        <Route
          path={translatedRoute('mtSettings')}
          element={
            <MtRoute
              buyer={<MtSettingsBuyerPageController />}
              breeder={<MtSettingsBreederPageController />}
            />
          }
        />
        <Route
          path={`${translatedRoute('mtPayments')}/*`}
          element={<MtRoute breeder={<MtPaymentsPageController />} />}
        />

        <Route
          path={translatedRoute('mtOwners')}
          element={
            <MtRoute
              buyer={<MtApplicationsBuyerPageController />}
              breeder={<MtApplicationsBreederPageController type={ApplicationType.OWNERS} />}
            />
          }
        />
        <Route
          path={`${translatedRoute('mtOwners')}:id`}
          element={<MtRoute breeder={<MtApplicationBreederPageController />} />}
        />
        <Route
          path={translatedRoute('mtWaitingList')}
          element={
            <MtRoute
              buyer={<MtApplicationsBuyerPageController />}
              breeder={<MtApplicationsBreederPageController type={ApplicationType.WAITING_LIST} />}
            />
          }
        />
        <Route
          path={`${translatedRoute('mtWaitingList')}:id`}
          element={
            <MtRoute
              breeder={<MtApplicationBreederPageController />}
              buyer={<MtApplicationBuyerPageController />}
            />
          }
        />
        <Route
          path={translatedRoute('mtApplications')}
          element={
            <MtRoute
              buyer={<MtApplicationsBuyerPageController />}
              breeder={<MtApplicationsBreederPageController type={ApplicationType.APPLICATIONS} />}
            />
          }
        />
        <Route
          path={`${translatedRoute('mtApplications')}:id`}
          element={
            <MtRoute
              buyer={<MtApplicationBuyerPageController />}
              breeder={<MtApplicationBreederPageController />}
            />
          }
        />
        <Route
          path={translatedRoute('mtApplicationCheckoutSuccess')}
          element={
            <MtRoute
              buyer={<MtApplicationAcceptSuccessPageController />}
              breeder={<MtApplicationsBreederPageController type={ApplicationType.APPLICATIONS} />}
            />
          }
        />
        <Route
          path={translatedRoute('mtApplicationCheckoutFailed')}
          element={
            <MtRoute
              buyer={<MtApplicationCheckoutFailedPageController />}
              breeder={<MtApplicationsBreederPageController type={ApplicationType.APPLICATIONS} />}
            />
          }
        />
        {/* Old Website Redirection */}
        <Route path="/nl/charter" element={<Navigate replace to={translatedRoute('chart')} />} />
        <Route
          path="/chiens-de-refuge"
          element={<Navigate replace to={translatedRoute('shelterList')} />}
        />
        <Route
          path="/nl/asielhonden"
          element={<Navigate replace to={translatedRoute('shelterList')} />}
        />
        <Route path="/rendez-vous/first-step" element={<OldProRedirectionController />} />
        <Route path="/nl/afspraak/first-step" element={<OldProRedirectionController />} />
        <Route path={translatedRoute('joinEducator')} element={<JoinEducatorPageController />} />
        <Route
          path={`${translatedRoute('educatorReview')}:id`}
          element={<EducatorReviewPageController />}
        />
        <Route
          path={`${translatedRoute('service')}:id`}
          element={<Navigate replace to={translatedRoute('proLanding')} />}
        />
        <Route
          path={`${translatedRoute('story')}`}
          element={<Navigate replace to={translatedRoute('homeLanding')} />}
        />
        <Route
          path="/inscription-professionnel/toiletteur"
          element={<Navigate replace to={translatedRoute('joinGroomer')} />}
        />
        <Route
          path="/nl/inschrijving-professional/trimmer"
          element={<Navigate replace to={translatedRoute('joinGroomer')} />}
        />
        <Route
          path="/inscription-professionnel/veterinaire"
          element={<Navigate replace to={translatedRoute('joinVet')} />}
        />
        <Route
          path="/nl/inschrijving-professional/dierenarts"
          element={<Navigate replace to={translatedRoute('joinVet')} />}
        />
        {/* {!isProduction() && (
        <Suspense fallback="loading...">
          <DesignSystemRoutes />
          </Suspense>
      )} */}
        <Route path="/404" element={<NotFoundPageController />} />
        <Route path="*" element={<RedirectWithStatus to="/404" status={404} />} />
      </Routes>
    </Suspense>
  );
};
